<template>
  <section>
    <app-dialog
      ref="actionTables"
      width="80%"
      :title="modalHeader"
      @close-dialog="closeActionTablesModal"
      persistent
    >
      <template #content>
        <app-custom-table
          :double-header="
            selectedAction === 'fund_expense_overview' ||
            selectedAction === 'currency_exchange' ||
            selectedAction === 'average_exchange_rate'
          "
          :top-headers="tableHeaders.top"
          :headers="tableHeaders.bottom"
          :items="tableItems"
          scroll-horizontally
          :currency-symbol="currency"
        ></app-custom-table>
      </template>
      <template #actions>
        <div class="flex items-end justify-end w-full">
          <app-primary-button
            :buttonLabel="$t('Close'.getTranslationKey())"
            @click="closeActionTablesModal"
          ></app-primary-button>
        </div>
      </template>
    </app-dialog>

    <app-dialog
      ref="lockDialog"
      :title="getLockingDialogTitleAndSubTitle.title"
      :subtitle="getLockingDialogTitleAndSubTitle.subTitle"
      width="500"
      persistent
    >
      <template #content>
        <div class="px-2 py-4 flex flex-col gap-2">
          <app-input-field
            id="lock-city"
            :label="$t('City'.getTranslationKey())"
            v-model="city"
            placeholder="Enter city"
          />
          <upload-signature
            :signee="isFullName"
            :position="isTeamLeadReview ? 'Team Leader' : 'Expert'"

          />
        </div>
      </template>
      <template #actions>
        <div class="flex items-end justify-end w-full">
          <v-col cols="3" class="">
            <app-secondary-button
              :buttonLabel="$t('Cancel'.getTranslationKey())"
              @click="closeLockMonth()"
            ></app-secondary-button>
          </v-col>
          <v-col cols="3" class="">
            <app-primary-button
              :buttonLabel="$t(lockDialogButtonLabel.getTranslationKey())"
              @click="handleLockOrUnlockMonth()"
              :loading="locking"
            ></app-primary-button>
          </v-col>
        </div>
      </template>
    </app-dialog>

    <app-alert
      :title="$t(getTranslatedStringKey(alertTitle))"
      @close-alert="alertState = false"
      :state="alertState"
      :type="alertType"
    />
  </section>
</template>
<script>
import { defineComponent } from "vue";
import API from "@/services/api";
import {
  approveDisapproveMonth,
  lockPmLocalMonth,
  teamLeadReview,
  unlockPmLocalMonth,
} from "@/services/endpoints";
import { formatDate, getTransString } from "@/services/helpers";
import {
  averageExchangeBottomHeaders,
  averageExchangeTopHeaders,
  BankToCashHeaders,
  currencyExchangeBottomHeaders,
  currencyExchangeTopHeaders,
  fundOverviewTableHeaders,
} from "@/services/fieldsUtils";
import UploadSignature from "@/components/shared/UploadSignature.vue";
export default defineComponent({
  components: { UploadSignature },
  data() {
    return {
      selectedAction: null,
      activeButton: null,
      buttonLabel: [
        "Bank/Cash Transfer",
        "Currency Exchange",
        "Average Exchange Rate",
        "Fund/Expense Overview",
      ],
      passedData: null,
      title: "",
      subTitle: "",
      dialogUploadState: false,
      imageUrl: null,
      selectedFile: null,
      showSignature: false,
      lockedMonth: false,
      alertType: "success",
      alertState: false,
      alertTitle: "",
      locking: false,
      city: "",
    };
  },
  props: {
    showLockButton: {
      type: Boolean,
      default: false,
    },
    month: {
      type: String,
      default: "",
    },
    year: {
      type: String,
      default: "",
    },
    currency: {
      type: String,
      default: "",
    },
    isTeamLeadReview: {
      type: Boolean,
      default: false,
    },
    monthNumber: {
      type: [String, Number],
      default: "",
    },
  },
  computed: {
    getLockingDialogTitleAndSubTitle() {
      if(this.selectedAction === "lock") {
        return {
          title: `${this.$t("Lock".getTranslationKey())} ${this.month} ${this.year}`,
          subTitle: "projectOverview.lockMessage"
        }
      } else if(this.selectedAction === "unlock") {
        return {
          title: `${this.$t("Unlock".getTranslationKey())} ${this.month} ${this.year}`,
          subTitle: "projectOverview.unlockMessage"
        }
      } else if(this.selectedAction === "approve") {
        return {
          title: `${this.$t("Approve".getTranslationKey())} ${this.month} ${this.year}`,
          subTitle: "projectOverview.approveSubtitle"
        }
      } else if(this.selectedAction === "disapprove") {
        return {
          title: `${this.$t("Disapprove".getTranslationKey())} ${this.month} ${this.year}`,
          subTitle: "projectOverview.disapproveSubtitle"
        }
      } else return {
        title: "",
        subTitle: ""
      };
    },
    lockDialogButtonLabel() {
      if(this.isTeamLeadReview) return "Approve";
      return (this.selectedAction?.charAt(0).toUpperCase() + this.selectedAction?.slice(1)) || "";
    },
    isAllBalanceInfoData() {
      return this.$store.getters["projectOverview/isAllBalanceInfoData"];
    },
    isFullName() {
      return this.$store.getters["auth/isfullName"];
    },
    signatureUrl() {
      return this.$store.getters["commonResources/signatureImageUrl"];
    },
    signatureDate() {
      return this.$store.getters["commonResources/signatureDate"];
    },
    isForeignEstablishmentId() {
      return this.$store.getters["pmLocalModules/isForeignEstablishmentID"];
    },
    isProjectId() {
      return this.$store.getters["pmLocalModules/isProjectId"];
    },
    modalHeader() {
      const headers = {
        bank_cash_transfer: "bankcash.bankToCash",
        currency_exchange: "currencyExchange.currencyExchange",
        fund_expense_overview: "projectOverview.fundsExp",
        average_exchange_rate: "projectOverview.avgExchange",
      };
      return headers[this.selectedAction] || "";
    },
    fundOverviewTopHeaders() {
      return [
        {
          key: "budget__budget_item__position",
          title: "Title",
          rowspan: 2,
          position: 0,
        },
        { key: "budget_quantity", title: "Quantity", rowspan: 2, position: 1 },
        { key: "plan", title: "Plan", colspan: 3 },
        {
          key: "month_year",
          title: `${this.$t(this.month.getTranslationKey())} ${this.year}`,
          noTranslation: true,
          colspan: 2,
        },
        { key: "total", title: "Total", colspan: 3 },
        { key: "new_monthly", title: "New Monthly", rowspan: 2, position: 10 },
      ];
    },
    tableHeaders() {
      if (this.selectedAction === "bank_cash_transfer") {
        return {
          top: [],
          bottom: BankToCashHeaders,
        };
      } else if (this.selectedAction === "currency_exchange") {
        return {
          top: currencyExchangeTopHeaders,
          bottom: currencyExchangeBottomHeaders,
        };
      } else if (this.selectedAction === "fund_expense_overview") {
        return {
          top: this.fundOverviewTopHeaders,
          bottom: fundOverviewTableHeaders,
        };
      } else if (this.selectedAction === "average_exchange_rate") {
        return {
          top: averageExchangeTopHeaders,
          bottom: averageExchangeBottomHeaders,
        };
      } else {
        return { top: [], bottom: [] };
      }
    },
    tableItems() {
      if (this.selectedAction === "bank_cash_transfer") {
        return (
          this.isAllBalanceInfoData?.bank_cash_transfer?.bank_transfers || []
        );
      } else if (this.selectedAction === "average_exchange_rate") {
        return this.isAllBalanceInfoData?.average_exchange_rate || [];
      } else if (this.selectedAction === "currency_exchange") {
        return (
          this.isAllBalanceInfoData?.currency_exchange_rate
            ?.currency_exchanges || []
        );
      } else if (this.selectedAction === "fund_expense_overview") {
        return this.isAllBalanceInfoData?.funds_overview;
      } else {
        return [];
      }
    },
  },
  methods: {
    getTranslatedStringKey(field) {
      return getTransString(field);
    },
    formattedDate(date) {
      return formatDate(date);
    },
    handleOnClick(label, index) {
      this.activeButton = label;
      if (this.buttonLabel[index] === "Bank/Cash Transfer") {
        this.passedData = this.isAllBalanceInfoData?.bank_cash_transfer;
      } else if (this.buttonLabel[index] === "Currency Exchange") {
        this.passedData = this.isAllBalanceInfoData?.currency_exchange_rate;
      } else if (this.buttonLabel[index] === "Average Exchange Rate") {
        this.passedData = this.isAllBalanceInfoData;
      } else if (this.buttonLabel[index] === "Fund/Expense Overview") {
        this.passedData = this.isAllBalanceInfoData?.funds_overview;
      }
    },
    closeLockMonth() {
      this.city = "";
      this.$emit("resetAction", null);
      this.$refs.lockDialog.showDialog = false;
    },
    openLockMonth(action) {
      this.selectedAction = action;
      this.$refs.lockDialog.showDialog = true;
    },
    openActionTablesModal(action) {
      this.selectedAction = action;
      this.$refs.actionTables.showDialog = true;
    },
    closeActionTablesModal() {
      this.$emit("resetAction", null);
      this.$refs.actionTables.showDialog = false;
    },
    handleLockMonth() {
      if(!this.signatureUrl) {
        this.alertState = true;
        this.alertType = "error";
        this.alertTitle = "Please append a signature before proceeding to lock the month";
        return;
      }
      this.locking = true;
      API()
        .post(
          `${lockPmLocalMonth}${this.isProjectId}/${this.isForeignEstablishmentId}/`,
          {
            month: this.month,
            year: this.year,
            city: this.city,
            locker_signature: this.signatureUrl,
            signature_date: new Date(this.signatureDate)?.toISOString()?.split("T")[0],
          }
        )
        .then((response) => {
          if (response.data.status === "success") {
            this.alertState = true;
            this.alertType = "success";
            this.alertTitle = response.data.details;
            this.$emit("getYearlyData");
            this.closeLockMonth();
          }
        })
        .catch((error) => {
          this.alertState = true;
          this.alertType = "error";
          this.alertTitle = error.response.data.detail;
        })
        .finally(() => {
          this.locking = false;
        });
    },
    handleUnlockMonth() {
      this.locking = true;
      API()
        .put(
          `${unlockPmLocalMonth}${this.isProjectId}/${this.isForeignEstablishmentId}/`,
          {
            month: this.month,
            year: this.year,
          }
        )
        .then((response) => {
          if (response.data.status === "success") {
            this.alertState = true;
            this.alertType = "success";
            this.alertTitle = response.data.details;
            this.$emit("getYearlyData");
            this.closeLockMonth();
          }
        })
        .catch((error) => {
          this.alertState = true;
          this.alertType = "error";
          this.alertTitle = error.response.data.detail;
        })
        .finally(() => {
          this.locking = false;
        });
    },
    handleApproveDisapproveMonth() {
      this.locking = true;
      let apiPromise;
      if (this.selectedAction === "approve") {
        apiPromise = API().post(`${approveDisapproveMonth}${this.isProjectId}/${this.isForeignEstablishmentId}/`, {
          month: this.month,
          year: this.year,
        });
      } else {
        apiPromise = API()
          .put(
            `${approveDisapproveMonth}${this.isProjectId}/${this.isForeignEstablishmentId}/`,
            {
              month: this.month,
              year: this.year,
            }
          )
      }
      apiPromise.then((response) => {
        if (response.data.status === "success") {
          this.alertState = true;
          this.alertType = "success";
          this.alertTitle = response.data.details;
          this.$emit("getYearlyData");
          this.closeLockMonth();
        }
      })
        .catch((error) => {
          this.alertState = true;
          this.alertType = "error";
          this.alertTitle = error.response.data.detail;
        })
        .finally(() => {
          this.locking = false;
        });
    },
    handleTeamLeadSign() {
      if (!this.signatureUrl) {
        this.alertState = true;
        this.alertType = "error";
        this.alertTitle = "Please append a signature before proceeding to lock the month";
        return;
      }
      this.locking = true;
      API()
        .put(
          `${teamLeadReview}${this.isProjectId}/${this.isForeignEstablishmentId}/`,
          {
            year: this.year,
            month: this.month,
            city: this.city,
            team_lead_signature: this.signatureUrl,
            signature_date: new Date(this.signatureDate)?.toISOString()?.split("T")[0],
          }
        )
        .then((response) => {
          if (response.data.status === "success") {
            this.alertState = true;
            this.alertType = "success";
            this.alertTitle = "This month has been signed by the team lead successfully and the controller has been notified for approval";
            this.$emit("getYearlyData");
            this.closeLockMonth();
          }
        })
        .catch((error) => {
          this.alertState = true;
          this.alertType = "error";
          this.alertTitle = error.response.data.detail;
        })
        .finally(() => {
          this.locking = false;
        });
    },
    handleLockOrUnlockMonth() {
      if (this.selectedAction === "lock") {
        if (this.isTeamLeadReview) {
          this.handleTeamLeadSign();
        } else {
          this.handleLockMonth();
        }
      } else if (this.selectedAction === "unlock") {
        this.handleUnlockMonth();
      } else {
        this.handleApproveDisapproveMonth()
      }
    },
    openFileInput() {
      this.showSignature = true;
      this.$refs.fileInput.click();
    },
  },
  watch: {
    selectedAction(newValue) {
      if (newValue === "lock") {
        this.$refs.lockDialog.showDialog = true;
      }
    },
  },
  created() {
    this.activeButton = this.buttonLabel[0];
    this.handleOnClick(this.buttonLabel[0], 0);
  },
});
</script>
<style scoped>
.active-button {
  background-color: #f4eae9 !important;
  border-radius: 8px !important;
}
</style>
